<template>
  <div :class="$options.name" v-bind="attrs.binds">
    <slot />
  </div>
</template>
<script lang="ts">
import { lateinit } from 'tg-commons'
import { bindEvents, LatLng, MapPane, unwrapStringEnumValue, type LabelEventMap, type LabelOptions, type LabelOverlay, type Marker, type WrapStringEnumValue } from 'tg-map-core'
import { defineComponent } from 'vue'
import { findAncestorComponentByType, optionalProp, stringEnumProp, useSplittedAttrs, type EventEmits, type Props } from '../../utils/vue-utils'
import TgMap from '../TgMap.vue'
import MapMixin from '../map-mixin'
import TgMarker, { type TgMarkerPublicInstance } from './TgMarker.vue'

const TgLabel = defineComponent({
  name: 'tg-label',
  mixins: [MapMixin],
  inheritAttrs: false,
  props: {
    position: optionalProp(LatLng),
    offset: optionalProp(Object),
    zIndex: optionalProp(Number),
    mapPane: stringEnumProp(MapPane),
  } satisfies Props<WrapStringEnumValue<
    Omit<LabelOptions, 'content'>,
    { mapPane: typeof MapPane }
  >>,
  emits: undefined as any as EventEmits<LabelEventMap>,
  setup(props, { attrs }) {
    return {
      attrs: useSplittedAttrs(attrs),
      overlay: lateinit<LabelOverlay>(),
    }
  },
  watch: {
    position(value) {
      this.overlay.setPosition(value)
    },
    offset(value) {
      // {@template hot_reload_prop_change_before_on_create}
      // 热重载时, this.overlay可能为空...故添加判空
      // 可能的原因是因为offset在模板中写成了纯对象表达式, 热重载重新渲染时, 会创建不同的对象, 导致触发change, 而change的触发发生在beforeMount之后, mounted/onCreate之前...
      // {@endtemplate}
      this.overlay?.setOffset(value)
    },
    zIndex(value) {
      this.overlay.setZIndex(value)
    },
    visible(value) {
      this.overlay.setVisible(value)
    },
    mapPane() {
      this.recreate()
    },
  },
  methods: {
    content(): HTMLElement {
      return this.$el as HTMLElement
    },
    marker(): Marker | undefined {
      return this.$marker()?.marker
    },
    $marker(): TgMarkerPublicInstance | undefined {
      return findAncestorComponentByType(TgMarker, this.$parent, TgMap)
    },
    getOptions(): LabelOptions {
      return {
        content: this.content(),
        position: this.position,
        offset: this.offset,
        zIndex: this.zIndex,
        mapPane: unwrapStringEnumValue(this.mapPane, MapPane),
      }
    },
  },
  onCreate() {
    this.overlay = this.$map.createLabel(this.getOptions())
    // 若父节点为marker, 则交由marker管理
    if (this.$marker()) {
      this.$marker()!.onAddLabel(this)
    } else {
      this.$map.addOverlay(this.overlay)
    }
    bindEvents(this.attrs.listeners, this.overlay, this.$emit)
  },
  onDestroy() {
    if (this.$marker()) {
      this.$marker()!.onRemoveLabel(this)
    } else {
      this.$map.removeOverlay(this.overlay)
    }
  },
})
type TgLabel = Omit<InstanceType<typeof TgLabel>, '$options'>
export default TgLabel
</script>
<style lang="scss">
// 只有移到.tg-map__map里面时才显示
.tg-map .tg-label {
  display: none;
}
.tg-map__map .tg-label {
  display: block;
}
</style>
